import uuid from 'uuid/v4';
import { AVAILABILITY_STATUS } from '@wix/chat-web';
import { availabilityHours } from '@wix/inbox-common';
import { ChatInitResult } from '../services/serverApi';
import { ThunkExtraArgument } from '../services/bootstrap/store-configuration';
import { initFirebase } from './firebase';
import {
  initChatSdk,
  updateBusinessChatRoomId,
  updateShardId,
  updateCorrelationId,
  updateIsChatSdkInit,
  updateCoreChatToken,
  updateAvailabilityStatus,
  updateIsBranded,
  updateAllowInput,
} from './index';
import wix from '../services/wix';
import firebaseChatService from '../services/firebaseChatService';
import { EDITOR_VIEW_MODE } from '../constants/editor-view-mode';
import { isPremium } from '../services/premium';
import { loadingScheduler } from '../services/loading-scheduler';
import { WidgetError } from '../WidgetError';

const trackAvailabilitySchedule = (availability, onChange, lastValue?) => {
  if (!availability) {
    return;
  }

  const {
    isInPeriod,
    isFuturePeriodAvailable,
  } = availabilityHours.calculateAvailability(availability);

  if (lastValue !== isInPeriod) {
    const availabilityStatus = isInPeriod
      ? AVAILABILITY_STATUS.AVAILABLE
      : AVAILABILITY_STATUS.AWAY;
    onChange(availabilityStatus);
  }

  if (isFuturePeriodAvailable) {
    const delay = 60 * 1000;
    window.setTimeout(() => {
      trackAvailabilitySchedule(availability, onChange, isInPeriod);
    }, delay);
  }
};

export const initApp = ({
  reconnectData = undefined as any,
  isBusinessChat = true,
} = {}) => {
  return async function(
    dispatch,
    getState,
    { visitCount, serverApi }: ThunkExtraArgument,
  ) {
    const deviceType: string = getState().deviceType;
    const isSiteMode = getState().viewMode === EDITOR_VIEW_MODE.SITE;
    const isInitialSiteModeInit = isSiteMode && !reconnectData;
    const correlationId: string = isInitialSiteModeInit
      ? getState().chatInitResult.correlationId
      : uuid();
    const chatInitResult = isInitialSiteModeInit
      ? (getState().chatInitResult as ChatInitResult)
      : await serverApi
          .init({
            deviceType,
            backend: 'CHAT',
            correlationId,
            ...reconnectData,
          })
          .catch(e => {
            throw new WidgetError("Couldn't get `init` data from server", e);
          });
    const {
      derivedChatroomId,
      authKey,
      options,
      messagePath,
      presencePath,
      visitorId,
      visitorInstanceId,
      location,
      shardId,
      timestamp,
      coreChatToken,
      availability,
      isBranded /* NOT (chat-premium || grandfathered) */,
    } = chatInitResult;
    dispatch(updateBusinessChatRoomId(derivedChatroomId));
    dispatch(updateShardId(shardId));
    dispatch(updateCorrelationId(correlationId));
    dispatch(updateCoreChatToken(coreChatToken));

    trackAvailabilitySchedule(availability, availabilityStatus => {
      dispatch(updateAvailabilityStatus(availabilityStatus));
    });

    const { isMandatory, isContact } = getState().isLcfMandatory;

    if (!reconnectData) {
      const { isInPeriod } = availabilityHours.calculateAvailability(
        availability,
      );
      const isAvailable = isInPeriod || !availability;

      dispatch(updateAllowInput(isContact || (isAvailable && !isMandatory)));
    }

    const initSdk = () => {
      dispatch(initChatSdk({ coreChatToken, isBusinessChat }));
      dispatch(updateIsChatSdkInit(true));
    };

    void loadingScheduler.onConnective().then(async () => {
      initSdk();

      if (!reconnectData) {
        await visitCount.incVisitCount();
      }

      const visitCountValue = visitCount.getVisitCount();

      const pageTitle = new Promise(resolve =>
        wix.syncPageTitle(title => resolve(title)),
      );
      await dispatch(
        initFirebase({
          initData: {
            authKey,
            options,
            messagePath,
            presencePath,
            visitorInstanceId,
            correlationId,
            visitorId,
            coreChatToken,
          },
          timestamp,
        }),
      );

      firebaseChatService.updatePresence(
        {
          deviceType,
          location,
          timestamp,
          visitCount: await visitCountValue,
          derivedChatroomId,
          pageName: await pageTitle,
          isContact,
          lastSeenTimestamp: firebaseChatService.getLastSeenTimestamp(),
        },
        false,
      );

      if (!reconnectData) {
        // TODO: both callbacks triggers on navigation, check if we can only rely on 'subscribeForMetadataChange'
        ['subscribeForNavigation', 'subscribeForMetadataChange'].forEach(f =>
          wix[f](pageName => {
            firebaseChatService.updatePresence({ pageName });
          }),
        );
      }
    });

    void loadingScheduler.onInteractive().then(async () => {
      if (isBranded !== undefined) {
        const isChatPremium = !isBranded;

        if (isChatPremium) {
          dispatch(updateIsBranded(isBranded));
        } else {
          isPremium()
            .then(_isPremium => {
              const isFreePackage = !_isPremium;
              dispatch(updateIsBranded(isFreePackage));
            })
            .catch(() => {
              /* do nothing */
            });
        }
      }
    });
  };
};
